<template>
    <div
        class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-xl ppv2-p-3 ppv2-shadow-sm hover:ppv2-shadow-md ppv2-transition-all ppv2-border ppv2-border-black ppv2-border-opacity-5 hover:ppv2-border-opacity-10"
    >
        <!-- Header: Customer and Date -->
        <div class="ppv2-flex ppv2-justify-between ppv2-items-start ppv2-mb-1">
            <div class="ppv2-flex ppv2-flex-col">
                <span class="poppinsSemiBold ppv2-text-sm ppv2-text-black ppv2-truncate ppv2-max-w-[160px]">
                    {{ displayName }}
                </span>
                <span class="ppv2-text-[10px] ppv2-text-black ppv2-text-opacity-40 poppinsMedium">
                    #{{ quote.order_id || 'N/A' }} - {{ quote.type_label || $t('quotes.card.default_type') }}
                </span>
            </div>
            <span class="ppv2-text-[10px] ppv2-text-black ppv2-text-opacity-40 poppinsMedium">
                {{ formatDate(quote.created_at) }}
            </span>
        </div>

        <!-- Body: Amount and Info -->
        <div class="ppv2-mb-1">
            <div class="ppv2-flex ppv2-flex-col">
                <div class="poppinsSemiBold ppv2-text-lg ppv2-text-black">
                    <template v-if="hasPromoApercu">
                        <s class="ppv2-text-black ppv2-text-opacity-40">{{ formatCurrency(quote.total_price || 0) }}</s>
                        {{ formatCurrency(quote.total_net) }} HT
                    </template>
                    <template v-else> {{ formatCurrency(quote.total_price || 0) }} HT </template>
                </div>
                <!-- Aperçu de la remise du code SÉLECTIONNÉ sur le devis (même rendu que la
                     caisse nomade) : elle ne sera réellement posée qu'à la conversion. -->
                <span
                    v-if="hasPromoApercu"
                    class="ppv2-inline-flex ppv2-items-center ppv2-self-start ppv2-gap-1 ppv2-px-2 ppv2-py-0.5 ppv2-rounded-full ppv2-text-[10px] poppinsMedium ppv2-mt-1"
                    style="background-color: rgba(74, 222, 128, 0.12); border: 1px solid rgba(74, 222, 128, 0.35); color: #4ade80"
                >
                    <Tag :size="11" /> {{ promoCodeDisplay }}
                    <span class="ppv2-opacity-80">· −{{ formatCurrency(quote.discount_total) }}</span>
                </span>
            </div>
            <div v-if="quote.creator" class="ppv2-text-[10px] ppv2-text-black ppv2-text-opacity-40 ppv2-mt-1">
                {{ $t('quotes.card.created_by', { name: quote.creator }) }}
            </div>
            <div v-if="quote.processed_info" class="ppv2-text-[10px] ppv2-text-black ppv2-text-opacity-40">
                {{ quote.processed_info }}
            </div>
            <!-- Relance Tag -->
            <div v-if="quote.last_relance_at" class="ppv2-mt-2">
                <span
                    class="ppv2-inline-flex ppv2-items-center ppv2-px-2 ppv2-py-0.5 ppv2-rounded ppv2-text-[10px] poppinsMedium ppv2-bg-blue-50 ppv2-text-blue-600 ppv2-border ppv2-border-blue-100"
                >
                    <Bell :size="10" class="ppv2-mr-1" />
                    {{ $t('quotes.card.relance_tag', { date: formatDate(quote.last_relance_at) }) }}
                </span>
            </div>
            <!-- Badge "Déjà facturé" pour les devis gagnés et facturés -->
            <div v-if="quote.status_label === 'Gagné' && isAlreadyInvoiced" class="ppv2-mt-2">
                <span
                    class="ppv2-inline-flex ppv2-items-center ppv2-px-2 ppv2-py-0.5 ppv2-rounded ppv2-text-[10px] poppinsMedium ppv2-bg-green-50 ppv2-text-green-700 ppv2-border ppv2-border-green-200"
                >
                    ✓ {{ $t('quotes.card.already_invoiced') }}
                </span>
            </div>
        </div>

        <!-- Footer: Action and Menu -->
        <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
            <!-- Devis gagné -->
            <template v-if="quote.status_label === 'Gagné'">
                <!-- Tant qu'une expérience à créneau n'a pas de date, on n'autorise PAS « Facturer » :
                     on affiche « Valider » qui ouvre la popup pour dater/réserver les créneaux. -->
                <button
                    v-if="quote.needs_validation"
                    @click="openValidation"
                    :disabled="loading"
                    class="ppv2-flex-grow ppv2-py-2 ppv2-rounded-lg ppv2-text-xs poppinsMedium ppv2-transition-all disabled:ppv2-opacity-50 ppv2-text-white"
                    style="background-color: #7d2dbd"
                >
                    {{ $t('quotes.card.actions.validate') }}
                </button>
                <!-- Tout est daté : deux boutons distincts (Facturer + Prévisualiser la facture). -->
                <template v-else>
                    <button
                        @click="invoiceQuote"
                        :disabled="loading || isAlreadyInvoiced"
                        class="ppv2-flex-grow ppv2-py-2 ppv2-rounded-lg ppv2-text-xs poppinsMedium ppv2-transition-all disabled:ppv2-opacity-50 ppv2-text-white"
                        style="background-color: #088f4c"
                    >
                        {{ $t('quotes.card.actions.invoice') }}
                    </button>
                    <button
                        v-if="isAlreadyInvoiced"
                        @click="previewInvoice"
                        :disabled="loading"
                        class="ppv2-flex-grow ppv2-py-2 ppv2-rounded-lg ppv2-text-xs poppinsMedium ppv2-transition-all disabled:ppv2-opacity-50 ppv2-border"
                        style="border-color: #088f4c; color: #088f4c"
                    >
                        {{ $t('quotes.card.actions.preview_invoice') }}
                    </button>
                </template>
            </template>

            <!-- Autres statuts : bouton d'action unique -->
            <button
                v-else-if="actionButton"
                @click="handleMainAction"
                :disabled="loading"
                :class="[
                    'ppv2-flex-grow ppv2-py-2 ppv2-rounded-lg ppv2-text-xs poppinsMedium ppv2-transition-all disabled:ppv2-opacity-50',
                    actionButton.class,
                ]"
            >
                {{ actionButton.label }}
            </button>

            <div class="ppv2-relative" ref="menuDropdown">
                <button
                    @click="showMenu = !showMenu"
                    class="ppv2-w-9 ppv2-h-9 ppv2-flex ppv2-items-center ppv2-justify-center ppv2-rounded-lg ppv2-transition-colors"
                    style="background-color: #8d8d8d42; color: #5d5d5d"
                >
                    <MoreVertical :size="16" />
                </button>

                <!-- Dropdown Menu -->
                <div
                    v-if="showMenu"
                    class="menuDropdown ppv2-absolute ppv2-bottom-full ppv2-right-0 ppv2-mb-2 ppv2-w-48 ppv2-bg-white ppv2-rounded-xl ppv2-shadow-xl ppv2-border ppv2-border-black ppv2-border-opacity-5 ppv2-py-2 ppv2-z-50 shadow-menu"
                >
                    <button
                        @click="viewQuote"
                        class="ppv2-w-full ppv2-px-4 ppv2-py-2.5 ppv2-text-sm ppv2-text-black ppv2-text-opacity-70 hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-flex ppv2-items-center ppv2-gap-3"
                    >
                        <Eye :size="16" /> {{ $t('quotes.card.view_edit') }}
                    </button>
                    <!-- <button @click="downloadQuote"
                        class="ppv2-w-full ppv2-px-4 ppv2-py-2.5 ppv2-text-sm ppv2-text-gray-700 hover:ppv2-bg-gray-50 ppv2-flex ppv2-items-center ppv2-gap-3">
                        <Download :size="16" /> {{ $t('quotes.card.download') }}
                    </button> -->
                    <!-- <button
                        @click="duplicateQuote"
                        class="ppv2-w-full ppv2-px-4 ppv2-py-2.5 ppv2-text-sm ppv2-text-gray-700 hover:ppv2-bg-gray-50 ppv2-flex ppv2-items-center ppv2-gap-3"
                    >
                        <Copy :size="16" /> Dupliquer
                    </button> -->
                    <div
                        v-if="!['Gagné', 'Perdu'].includes(quote.status_label)"
                        class="ppv2-h-[1px] ppv2-bg-black ppv2-bg-opacity-[5%] ppv2-my-1"
                    ></div>
                    <!-- « Gagné » n'est pas proposé sur un BROUILLON : le client ne l'a pas
                         reçu, il n'a rien pu accepter. Le devis passe d'abord par « Envoyé ».
                         Le serveur refuse de toute façon la transition — ceci évite juste de
                         montrer une action qui finirait en erreur. -->
                    <button
                        v-if="
                            quote.resource === 'invoice' &&
                            !['Gagné', 'Perdu', 'Expiré', 'Brouillon'].includes(quote.status_label)
                        "
                        @click="markAsWon"
                        :disabled="loading"
                        class="ppv2-w-full ppv2-px-4 ppv2-py-2.5 ppv2-text-sm ppv2-text-[#088F4C] hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-flex ppv2-items-center ppv2-gap-3 disabled:ppv2-opacity-50"
                    >
                        <CheckCircle :size="16" /> {{ $t('quotes.card.mark_won') }}
                    </button>
                    <button
                        v-if="quote.resource === 'invoice' && !['Gagné', 'Perdu', 'Expiré'].includes(quote.status_label)"
                        @click="markAsLost"
                        :disabled="loading"
                        class="ppv2-w-full ppv2-px-4 ppv2-py-2.5 ppv2-text-sm ppv2-text-[#E95830] hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-flex ppv2-items-center ppv2-gap-3 disabled:ppv2-opacity-50"
                    >
                        <XCircle :size="16" /> {{ $t('quotes.card.mark_lost') }}
                    </button>
                    <!-- <button v-if="quote.status_label === 'Brouillon'" @click="deleteQuote"
                        class="ppv2-w-full ppv2-px-4 ppv2-py-2.5 ppv2-text-sm ppv2-text-[#E95830] hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-flex ppv2-items-center ppv2-gap-3">
                        <Trash2 :size="16" /> Supprimer
                    </button> -->
                </div>
            </div>
        </div>

        <!-- Modale « expériences sans date » : bloque conversion/facturation tant qu'une
             prestation à créneau n'est pas datée (garde-fou serveur QuoteExperienceDateGuard). -->
        <ExperienceDatesModal
            :show="showExpDatesModal"
            :experiences="undatedExperiences"
            :subtitle="expModalSubtitle"
            :order-id="Number(quote.order_id) || null"
            @close="showExpDatesModal = false"
            @confirm="retryPendingValidation"
        />
    </div>
</template>

<script>
import { MoreVertical, Eye, Download, Copy, CheckCircle, XCircle, RotateCw, Trash2, Bell, Tag } from 'lucide-vue'
import ExperienceDatesModal from './ExperienceDatesModal.vue'

export default {
    name: 'DevisKanbanCard',
    components: {
        MoreVertical,
        Eye,
        Download,
        Copy,
        CheckCircle,
        XCircle,
        RotateCw,
        Trash2,
        Bell,
        Tag,
        ExperienceDatesModal,
    },
    props: {
        quote: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showMenu: false,
            loading: false,
            // Modale « expériences sans date » (garde-fou avant validation/facturation).
            showExpDatesModal: false,
            undatedExperiences: [],
            // Action qui a été bloquée par le garde et à relancer une fois les dates saisies :
            // 'won' (markAsWon → validate-api, 400) ou 'invoice' (invoiceQuote → invoice, 422).
            pendingValidationAction: null,
            invoicedLocally: false,
        }
    },
    computed: {
        displayName() {
            const user = this.quote.user
            if (!user) return this.$t('quotes.card.deleted_client')
            const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
            return name || this.$t('quotes.card.deleted_client')
        },
        // Sous-titre de la modale des dates : « #numéro · client · montant HT ».
        expModalSubtitle() {
            const num = this.quote.quote_serial_number || this.quote.order_id || 'N/A'
            return `#${num} · ${this.displayName} · ${this.formatCurrency(this.quote.total_price || 0)} HT`
        },
        actionButton() {
            switch (this.quote.status_label) {
                case 'Demande':
                    return {
                        label: this.$t('quotes.card.actions.respond'),
                        class: 'ppv2-bg-[#1F3CCD] ppv2-text-white hover:ppv2-opacity-90',
                    }
                case 'Envoyé':
                    return {
                        label: this.$t('quotes.card.actions.relaunch'),
                        class: 'ppv2-bg-[#0075FF] ppv2-text-white hover:ppv2-opacity-90',
                    }
                case 'Expiré':
                    return {
                        label: this.$t('quotes.card.actions.relaunch'),
                        class: 'ppv2-bg-[#DBA800] ppv2-text-white hover:ppv2-opacity-90',
                    }
                case 'Gagné':
                    // Géré dans le template : deux boutons distincts (Facturer + Prévisualiser).
                    return null
                case 'Perdu':
                    return {
                        label: this.$t('quotes.card.actions.reopen'),
                        class: 'ppv2-bg-[#E1E1E1] !ppv2-text-black ppv2-text-opacity-70 hover:ppv2-opacity-90',
                    }
                case 'Brouillon':
                    return {
                        label: this.$t('quotes.card.actions.send'),
                        class: 'ppv2-bg-[#9C9C9C] ppv2-text-white hover:ppv2-opacity-90',
                    }
                default:
                    return null
            }
        },
        isAlreadyInvoiced() {
            return this.invoicedLocally || (this.quote.deferred_invoice_count || 0) > 0
        },
        // Aperçu de remise présent : code sélectionné valide + remise > 0. La carte affiche alors
        // le prix barré + le net, comme la caisse nomade.
        hasPromoApercu() {
            return !!this.quote.promotion && Number(this.quote.discount_total) > 0 && this.quote.total_net != null
        },
        // Une « remise instantanée » (is_special) n'affiche jamais son code généré — seulement le
        // titre (règle de la caisse). Un code normal affiche son code.
        promoCodeDisplay() {
            const p = this.quote.promotion
            if (!p) return ''
            return p.is_special ? p.title : p.code
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            const day = date.getDate()
            const monthNames = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
            return `${day} ${monthNames[date.getMonth()]}`
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount || 0)
        },
        handleClickOutside(event) {
            if (this.$refs.menuDropdown && !this.$refs.menuDropdown.contains(event.target)) {
                this.showMenu = false
            }
        },
        handleMainAction() {
            if (this.quote.status_label === 'Demande') {
                this.respondToRequest()
            } else if (this.quote.status_label === 'Brouillon') {
                this.sendQuote()
            } else if (this.quote.status_label === 'Envoyé' || this.quote.status_label === 'Expiré') {
                this.relaunchQuote()
            } else if (this.quote.status_label === 'Perdu') {
                this.reopenQuote()
            } else if (this.quote.status_label === 'Gagné') {
                // Facture déjà existante : on prévisualise au lieu de re-facturer.
                if (this.isAlreadyInvoiced) {
                    this.previewInvoice()
                } else {
                    this.invoiceQuote()
                }
            } else if (this.quote.resource === 'answer') {
                this.$emit('open-detail')
            } else if (this.quote.order_id) {
                window.location.href = `/back-office/commandes/${this.quote.order_id}/descriptif`
            }
        },
        async invoiceQuote() {
            if (this.loading || !this.quote.order_id) return
            this.loading = true
            try {
                const response = await this.axios.post(`/back-office/devis/${this.quote.order_id}/invoice`)
                this.$toast.success(this.$t('quotes.card.toasts.invoiced'))
                // Le code promo choisi n'a pas pu être appliqué (expiré/désactivé/inapplicable) :
                // la facture a été créée sans la remise → on avertit le commercial.
                const promoWarning = response?.data?.data?.promo_warning
                if (promoWarning) {
                    this.$toast.warning(promoWarning)
                }
                this.invoicedLocally = true
                this.$emit('reload')
            } catch (error) {
                const undated = error.response?.data?.undated_experiences
                // Expériences sans date (422) : modale « définir les dates » avant de facturer.
                if (error.response?.status === 422 && Array.isArray(undated) && undated.length) {
                    this.openExpDatesModal(undated, 'invoice')
                } else if (error.response?.status === 500 && this.isDeferredInvoiceExistsError(error)) {
                    // Une facture à paiement ultérieur existe déjà : on bascule le bouton
                    // en "Prévisualiser la facture" au lieu d'afficher l'erreur.
                    this.invoicedLocally = true
                    this.previewInvoice()
                    // Données périmées : on recharge pour récupérer deferred_invoice_id
                    // et basculer définitivement la carte en "Prévisualiser la facture".
                    this.$emit('reload')
                } else {
                    this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
                }
            } finally {
                this.loading = false
            }
        },
        isDeferredInvoiceExistsError(error) {
            const message = error.response?.data?.message || ''
            return message.includes('paiement ultérieur existe déjà')
        },
        previewInvoice() {
            if (!this.quote.order_id) return
            // Endpoint dédié : le backend choisit le bon document (facture à paiement
            // ultérieur existante via makeValidatedOrderInvoice, sinon facture standard).
            // Évite l'exception "The order booking is not confirmed" et ne dépend pas
            // d'un id présent dans des données potentiellement périmées.
            window.open(`/back-office/devis/${this.quote.order_id}/invoice-preview`, '_blank')
        },
        async respondToRequest() {
            if (this.loading) return
            this.loading = true
            try {
                if (this.quote.resource === 'answer') {
                    // Demande issue d'un formulaire : crée un Brouillon avec la demande dans le post-it.
                    await this.axios.post(`/back-office/devis-v3/answer/${this.quote.id}/respond`)
                } else if (this.quote.order_id) {
                    // Devis marqué "demandé" : bascule simplement en Brouillon.
                    await this.axios.post(`/back-office/devis/${this.quote.order_id}/to-draft`)
                }
                this.$toast.success(this.$t('quotes.card.toasts.draft_created'))
                this.$emit('reload')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
            } finally {
                this.loading = false
            }
        },
        async sendQuote() {
            if (this.loading || !this.quote.order_id) return
            this.loading = true
            try {
                await this.axios.get(`/back-office/devis/${this.quote.order_id}/sent-quote`)
                this.$toast.success(this.$t('quotes.card.toasts.sent'))
                this.$emit('reload')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
            } finally {
                this.loading = false
            }
        },
        async relaunchQuote() {
            if (this.loading || !this.quote.order_id) return
            this.loading = true
            try {
                await this.axios.post(`/back-office/devis/${this.quote.order_id}/relaunch`)
                this.$toast.success(this.$t('quotes.card.toasts.relaunched'))
                this.$emit('reload')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
            } finally {
                this.loading = false
            }
        },
        async reopenQuote() {
            if (this.loading || !this.quote.order_id) return
            this.loading = true
            try {
                await this.axios.post(`/back-office/devis/${this.quote.order_id}/reopen`)
                this.$toast.success(this.$t('quotes.card.toasts.reopened'))
                this.$emit('reload')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
            } finally {
                this.loading = false
            }
        },
        // Même règle que la vue tableau : Brouillon / Envoyé / Expiré se rouvrent dans
        // l'écran de rédaction V3, un devis clos (Gagné / Perdu) reste en consultation.
        isEditableQuote() {
            return this.quote.resource === 'invoice' && ['Brouillon', 'Envoyé', 'Expiré'].includes(this.quote.status_label)
        },
        viewQuote() {
            if (this.quote.resource === 'answer') {
                this.$emit('open-detail')
                this.showMenu = false
            } else if (this.quote.order_id) {
                // Brouillon / Envoyé / Expiré : éditeur pleinement éditable.
                // Gagné (accepté/signé) : même éditeur en mode verrouillé (Create.vue
                // s'appuie sur quote_accepted_at, seule la case Option reste modifiable).
                // Perdu : consultation seule (descriptif).
                const toEditor = this.isEditableQuote() || this.quote.status_label === 'Gagné'
                window.location.href = toEditor
                    ? `/back-office/v3/devis/create/${this.quote.order_id}`
                    : `/back-office/commandes/${this.quote.order_id}/descriptif`
            }
        },
        downloadQuote() {
            this.$toast.info(this.$t('quotes.card.toasts.downloading'))
            this.showMenu = false
        },
        duplicateQuote() {
            this.$toast.info(this.$t('quotes.card.toasts.duplicating'))
            this.showMenu = false
        },
        async markAsWon() {
            if (this.loading) return
            this.loading = true
            try {
                await this.axios.post(`/back-office/devis/${this.quote.order_id}/validate-api`)
                this.$toast.success(this.$t('quotes.card.toasts.marked_won'))
                this.$emit('reload')
            } catch (error) {
                // Expériences sans date (400) : on ouvre la modale « définir les dates » au lieu
                // d'un simple toast d'erreur.
                const undated = error.response?.data?.errors?.undated_experiences
                if (error.response?.status === 400 && Array.isArray(undated) && undated.length) {
                    this.openExpDatesModal(undated, 'won')
                } else {
                    this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
                }
            } finally {
                this.loading = false
            }
            this.showMenu = false
        },
        openExpDatesModal(list, action) {
            this.showMenu = false
            this.pendingValidationAction = action
            // On conserve TOUS les champs enrichis par le garde (item_id, slug, ticket_selections…) :
            // la modale en a besoin pour rouvrir le sélecteur de créneau sans recharger la ligne.
            // On PRÉSERVE la date renvoyée par le serveur : les lignes déjà datées s'affichent
            // en vert (lecture seule), seules les non datées (date null) portent l'action.
            this.undatedExperiences = list.map((e) => ({ ...e, date: e.date ?? null }))
            this.showExpDatesModal = true
        },
        // Bouton « Valider » d'un devis Gagné : on charge les expériences à créneau et on ouvre la
        // popup pour dater/réserver, SANS convertir. Une fois tout daté, le confirm rafraîchit la
        // liste (needs_validation repasse à false → le bouton devient « Facturer »).
        async openValidation() {
            if (this.loading) return
            this.loading = true
            try {
                const { data } = await this.axios.get(`/back-office/devis/${this.quote.order_id}/slot-experiences`)
                const list = data?.data?.slot_experiences || []
                this.openExpDatesModal(list, 'validate-only')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
            } finally {
                this.loading = false
            }
        },
        // Toutes les expériences ont été datées sur place :
        //  - 'validate-only' (bouton « Valider ») : on ne convertit pas, on rafraîchit simplement ;
        //  - 'won' / 'invoice' : on relance l'action initialement bloquée par le garde.
        retryPendingValidation() {
            this.showExpDatesModal = false
            const action = this.pendingValidationAction
            this.pendingValidationAction = null
            if (action === 'won') {
                this.markAsWon()
            } else if (action === 'invoice') {
                this.invoiceQuote()
            } else {
                // validate-only : la datation a déjà matérialisé les réservations, rien à convertir.
                this.$emit('reload')
            }
        },
        async markAsLost() {
            if (this.loading) return
            this.loading = true
            try {
                await this.axios.post(`/back-office/devis/${this.quote.order_id}/refuse`)
                this.$toast.success(this.$t('quotes.card.toasts.marked_lost'))
                this.$emit('reload')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
            } finally {
                this.loading = false
            }
            this.showMenu = false
        },
        deleteQuote() {
            this.$toast.info(this.$t('quotes.card.toasts.deleting'))
            this.showMenu = false
        },
    },
}
</script>

<style scoped>
.shadow-menu {
    box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
</style>
